// Mock Data Generator for Testing
// Generates realistic test data for development and testing

import { 
  Lender, 
  Series, 
  NAVHistory, 
  Position, 
  Crystallisation,
  Redemption,
  MonthlyReport,
  User
} from '../types';

const firstNames = ['John', 'Sarah', 'Michael', 'Emily', 'David', 'Jennifer', 'William', 'Jessica', 'Robert', 'Lisa'];
const lastNames = ['Wong', 'Chan', 'Lee', 'Tan', 'Chen', 'Ng', 'Lam', 'Cheung', 'Cheng', 'Ho'];
const companies = ['Alpha Investments', 'Beta Holdings', 'Gamma Partners', 'Delta Capital', 'Epsilon Fund'];
const banks = ['HSBC', 'Standard Chartered', 'Citibank', 'Bank of China (Hong Kong)', 'DBS Bank'];

const stocks = [
  { symbol: 'AAPL', name: 'Apple Inc.', basePrice: 175 },
  { symbol: 'MSFT', name: 'Microsoft Corporation', basePrice: 380 },
  { symbol: 'GOOGL', name: 'Alphabet Inc.', basePrice: 140 },
  { symbol: 'AMZN', name: 'Amazon.com Inc.', basePrice: 145 },
  { symbol: 'NVDA', name: 'NVIDIA Corporation', basePrice: 480 },
  { symbol: 'META', name: 'Meta Platforms Inc.', basePrice: 350 },
  { symbol: 'TSLA', name: 'Tesla Inc.', basePrice: 240 },
  { symbol: 'JPM', name: 'JPMorgan Chase', basePrice: 150 },
  { symbol: 'V', name: 'Visa Inc.', basePrice: 260 },
  { symbol: 'WMT', name: 'Walmart Inc.', basePrice: 160 }
];

/**
 * Generate random number between min and max
 */
const randomBetween = (min: number, max: number): number => {
  return Math.random() * (max - min) + min;
};

/**
 * Generate random integer between min and max
 */
const randomInt = (min: number, max: number): number => {
  return Math.floor(randomBetween(min, max));
};

/**
 * Pick random item from array
 */
const randomPick = <T>(array: T[]): T => {
  return array[randomInt(0, array.length)];
};

/**
 * Generate random date within past N days
 */
const randomDatePast = (daysAgo: number): Date => {
  const now = new Date();
  const past = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
  return new Date(randomBetween(past.getTime(), now.getTime()));
};

/**
 * Generate random date in future N days
 */
const randomDateFuture = (daysAhead: number): Date => {
  const now = new Date();
  const future = new Date(now.getTime() + daysAhead * 24 * 60 * 60 * 1000);
  return new Date(randomBetween(now.getTime(), future.getTime()));
};

/**
 * Generate mock lender
 */
export const generateMockLender = (id?: string): Lender => {
  const isCompany = Math.random() > 0.5;
  const lenderId = id || `lender_${Date.now()}_${randomInt(1000, 9999)}`;
  
  let fullName: string;
  if (isCompany) {
    fullName = `${randomPick(companies)} Limited`;
  } else {
    fullName = `${randomPick(firstNames)} ${randomPick(lastNames)}`;
  }
  
  return {
    id: lenderId,
    fullName,
    email: `${fullName.toLowerCase().replace(/\s+/g, '.')}@example.com`,
    phone: `+852 ${randomInt(5000, 9999)} ${randomInt(1000, 9999)}`,
    address: `Unit ${randomInt(1, 30)}${String.fromCharCode(65 + randomInt(0, 5))}, ${randomInt(1, 50)}/F, ${randomPick(['Central', 'Admiralty', 'Wan Chai', 'Causeway Bay'])}, Hong Kong`,
    idNumber: isCompany ? `BR${randomInt(1000000, 9999999)}` : `HK${String.fromCharCode(65 + randomInt(0, 26))}${randomInt(100000, 999999)}`,
    bankDetails: {
      bankName: randomPick(banks),
      accountName: fullName,
      accountNumber: `${randomInt(100, 999)}-${randomInt(100000, 999999)}-${randomInt(100, 999)}`,
      bankCode: `00${randomInt(1, 9)}`,
      branchCode: `${randomInt(100, 999)}`
    },
    createdAt: randomDatePast(365),
    updatedAt: randomDatePast(30),
    totalInvested: 0 // Will be calculated from series
  };
};

/**
 * Generate mock series for a lender
 */
export const generateMockSeries = (
  lenderId: string,
  principalAmount?: number,
  issueDate?: Date
): Series => {
  const seriesId = `series_${Date.now()}_${randomInt(1000, 9999)}`;
  const principal = principalAmount || randomInt(100000, 2000000);
  const issue = issueDate || randomDatePast(365);
  const closing = new Date(issue);
  closing.setDate(closing.getDate() - randomInt(1, 5)); // Closing a few days before issue
  
  const lockupEnd = new Date(issue);
  lockupEnd.setMonth(lockupEnd.getMonth() + 12);
  
  // Current NAV is principal +/- some performance
  const performanceMultiplier = randomBetween(0.85, 1.25); // -15% to +25%
  const currentNAV = principal * performanceMultiplier;
  
  // High water mark is the highest NAV achieved
  const highWaterMark = Math.max(principal, currentNAV * randomBetween(0.95, 1.05));
  
  return {
    id: seriesId,
    lenderId,
    principalAmount: principal,
    closingDate: closing,
    issueDate: issue,
    lockupEnd,
    highWaterMark,
    status: 'active',
    currentNAV,
    capitalizedInterest: 0, // Initialize mock series with no capitalized interest
    createdAt: closing,
    updatedAt: new Date()
  };
};

/**
 * Generate mock portfolio positions
 */
export const generateMockPositions = (targetValue: number): Position[] => {
  const numPositions = randomInt(5, 10);
  const positions: Position[] = [];
  let remainingValue = targetValue;
  
  for (let i = 0; i < numPositions; i++) {
    const stock = randomPick(stocks);
    const isLastPosition = i === numPositions - 1;
    
    // Allocate percentage of remaining value
    const allocation = isLastPosition ? 1 : randomBetween(0.05, 0.25);
    const positionValue = remainingValue * allocation;
    
    const price = stock.basePrice * randomBetween(0.9, 1.1);
    const quantity = Math.floor(positionValue / price);
    const actualValue = quantity * price;
    
    positions.push({
      symbol: stock.symbol,
      name: stock.name,
      quantity,
      price,
      value: actualValue,
      assetType: 'stock',
      currency: 'USD'
    });
    
    remainingValue -= actualValue;
  }
  
  return positions;
};

/**
 * Generate mock NAV history entry
 */
export const generateMockNAVHistory = (
  date: Date,
  totalNAV: number,
  userId: string = 'admin'
): NAVHistory => {
  const positions = generateMockPositions(totalNAV * 0.95); // 95% in stocks
  const cash = totalNAV - positions.reduce((sum, p) => sum + p.value, 0);
  
  return {
    id: `nav_${date.getTime()}_${randomInt(1000, 9999)}`,
    date,
    totalNAV,
    ibAccountValue: totalNAV * randomBetween(0.98, 1.00),
    manualAdjustments: [],
    positions,
    cash,
    createdBy: userId,
    createdAt: date
  };
};

/**
 * Generate mock crystallisation
 */
export const generateMockCrystallisation = (
  series: Series,
  lenderId: string,
  userId: string = 'admin'
): Crystallisation => {
  const endDate = new Date();
  const startDate = new Date(endDate);
  startDate.setFullYear(startDate.getFullYear() - 1);
  
  const startNAV = series.principalAmount;
  const endNAV = series.currentNAV;
  const grossProfit = endNAV - startNAV;
  
  // Simple annualized return
  const returnRate = grossProfit / startNAV;
  const annualisedReturnRate = returnRate; // Assuming 1 year period
  
  const hurdleRateMet = annualisedReturnRate > 0.03;
  const highWaterMarkExceeded = endNAV > series.highWaterMark;
  
  let performanceFee = 0;
  let interest = grossProfit;
  
  if (grossProfit > 0 && hurdleRateMet && highWaterMarkExceeded) {
    performanceFee = grossProfit * 0.20;
    interest = grossProfit - performanceFee;
  }
  
  return {
    id: `cryst_${Date.now()}_${randomInt(1000, 9999)}`,
    date: endDate,
    seriesId: series.id,
    lenderId,
    performancePeriodStart: startDate,
    performancePeriodEnd: endDate,
    startNAV,
    endNAV,
    grossProfit,
    annualisedReturnRate,
    hurdleRateMet,
    highWaterMarkExceeded,
    performanceFee,
    interest,
    previousHighWaterMark: series.highWaterMark,
    newHighWaterMark: highWaterMarkExceeded ? endNAV : series.highWaterMark,
    paymentMethod: Math.random() > 0.7 ? 'cash_payment' : 'capitalised',
    paymentStatus: 'capitalised',
    createdAt: endDate,
    createdBy: userId
  };
};

/**
 * Generate mock user
 */
export const generateMockUser = (role: 'admin' | 'investor', lenderId?: string): User => {
  const isAdmin = role === 'admin';
  
  return {
    id: `user_${Date.now()}_${randomInt(1000, 9999)}`,
    email: isAdmin ? 'admin@maverickcapital.com' : `investor${randomInt(1, 99)}@example.com`,
    role,
    lenderId: isAdmin ? undefined : lenderId,
    displayName: isAdmin ? 'Fund Manager' : `${randomPick(firstNames)} ${randomPick(lastNames)}`,
    createdAt: randomDatePast(180),
    lastLogin: randomDatePast(7)
  };
};

/**
 * Generate complete mock dataset for testing
 */
export const generateCompleteMockDataset = () => {
  // Generate lenders
  const numLenders = 8;
  const lenders: Lender[] = [];
  
  for (let i = 0; i < numLenders; i++) {
    lenders.push(generateMockLender());
  }
  
  // Generate series for each lender (some lenders have multiple series)
  const allSeries: Series[] = [];
  const seriesPerLender: { [lenderId: string]: Series[] } = {};
  
  lenders.forEach((lender) => {
    const numSeries = randomInt(1, 3); // 1-3 series per lender
    const lenderSeries: Series[] = [];
    
    for (let i = 0; i < numSeries; i++) {
      const series = generateMockSeries(lender.id);
      lenderSeries.push(series);
      allSeries.push(series);
    }
    
    seriesPerLender[lender.id] = lenderSeries;
    
    // Update lender total invested
    lender.totalInvested = lenderSeries.reduce((sum, s) => sum + s.principalAmount, 0);
  });
  
  // Calculate total pool NAV
  const totalPoolNAV = allSeries.reduce((sum, s) => sum + s.currentNAV, 0);
  
  // Generate NAV history (last 90 days)
  const navHistory: NAVHistory[] = [];
  const today = new Date();
  
  for (let i = 90; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    // Simulate NAV growth with some volatility
    const daysFromStart = 90 - i;
    const growth = 1 + (daysFromStart / 90) * 0.15; // 15% growth over 90 days
    const volatility = randomBetween(0.98, 1.02);
    const dailyNAV = totalPoolNAV * growth * volatility;
    
    navHistory.push(generateMockNAVHistory(date, dailyNAV));
  }
  
  // Generate some crystallisations (for series older than 1 year)
  const crystallisations: Crystallisation[] = [];
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  
  allSeries.forEach((series) => {
    if (series.issueDate < oneYearAgo) {
      crystallisations.push(generateMockCrystallisation(series, series.lenderId));
    }
  });
  
  // Generate users
  const users: User[] = [];
  
  // Admin user
  users.push({
    id: 'admin_user',
    email: 'admin@maverickcapital.com',
    role: 'admin',
    displayName: 'Fund Manager',
    createdAt: randomDatePast(365),
    lastLogin: new Date()
  });
  
  // Investor users for each lender
  lenders.forEach((lender) => {
    users.push(generateMockUser('investor', lender.id));
  });
  
  return {
    lenders,
    series: allSeries,
    seriesPerLender,
    navHistory,
    crystallisations,
    users,
    totalPoolNAV,
    summary: {
      totalLenders: lenders.length,
      totalSeries: allSeries.length,
      totalAUM: totalPoolNAV,
      totalPrincipal: allSeries.reduce((sum, s) => sum + s.principalAmount, 0),
      averageReturn: ((totalPoolNAV / allSeries.reduce((sum, s) => sum + s.principalAmount, 0)) - 1) * 100
    }
  };
};

/**
 * Generate time series NAV data for charts
 */
export const generateNAVTimeSeries = (
  startValue: number,
  days: number,
  targetReturn: number = 0.12 // 12% annual return
): { date: Date; nav: number }[] => {
  const data: { date: Date; nav: number }[] = [];
  const today = new Date();
  
  for (let i = days; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    const progress = (days - i) / days;
    const expectedGrowth = 1 + (targetReturn * progress);
    const dailyVolatility = randomBetween(0.99, 1.01);
    const nav = startValue * expectedGrowth * dailyVolatility;
    
    data.push({ date, nav });
  }
  
  return data;
};

/**
 * Get mock IB account snapshot
 */
export const generateMockIBSnapshot = (totalValue: number) => {
  const positions = generateMockPositions(totalValue * 0.95);
  const stockValue = positions.reduce((sum, p) => sum + p.value, 0);
  const cashBalance = totalValue - stockValue;
  
  return {
    accountId: 'DU123456',
    timestamp: new Date(),
    netLiquidation: totalValue,
    cashBalance,
    stockValue,
    optionValue: 0,
    positions,
    unrealizedPnL: randomBetween(-10000, 50000),
    realizedPnL: randomBetween(5000, 100000)
  };
};

export default {
  generateMockLender,
  generateMockSeries,
  generateMockPositions,
  generateMockNAVHistory,
  generateMockCrystallisation,
  generateMockUser,
  generateCompleteMockDataset,
  generateNAVTimeSeries,
  generateMockIBSnapshot
};
